import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, Inbox, Wrench, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Admin notification bell with popover.
 * Fetches unread school observations and shows a list.
 * Clicking an item navigates to the budget detail (which marks it read).
 */
export default function NotificationBell({ compact = false }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/dashboard/stats");
      setCount(r.data?.unread_observations ?? 0);
    } catch {}
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/observations/unread");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const goTo = (it) => {
    setOpen(false);
    if (it.type === "service_request") {
      nav("/solicitacoes");
    } else {
      nav(`/orcamentos/${it.budget_id}`);
    }
    // Give backend a tick to mark as read, then refresh count
    setTimeout(loadCount, 800);
  };

  const markOne = async (it, e) => {
    e.stopPropagation();
    try {
      if (it.type === "service_request") {
        await api.post(`/service-requests/${it.id}/dismiss`);
      } else {
        await api.post(`/observations/${it.id}/mark-read`);
      }
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      loadCount();
    } catch {
      toast.error("Falha ao marcar como lida");
    }
  };

  const markAll = async () => {
    try {
      const r = await api.post("/observations/mark-all-read");
      toast.success(`${(r.data?.observations || 0) + (r.data?.requests || 0)} marcada(s) como lida(s)`);
      setItems([]);
      loadCount();
    } catch {
      toast.error("Falha ao marcar todas como lidas");
    }
  };

  const Icon = Bell;
  const size = compact ? "w-6 h-6" : "w-5 h-5";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={compact ? "relative p-2" : "relative p-2 rounded-lg hover:bg-slate-100"}
          data-testid={compact ? "mobile-bell" : "notifications-btn"}
          aria-label="Notificações"
        >
          <Icon className={`${size} text-[#1e3a8a]`} strokeWidth={2.2} />
          {count > 0 && (
            <span
              className={`absolute ${compact ? "top-1 right-1" : "top-1 right-1"} min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1`}
              data-testid="notifications-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-popover">
        <div className="px-3 py-2.5 border-b border-slate-100 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-heading text-sm font-black text-[#1e3a8a] flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notificações
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {count > 0 ? `${count} sem leitura` : "Tudo em dia"}
            </p>
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={markAll}
              data-testid="notif-mark-all"
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#7c3aed] hover:text-[#5b21b6]"
              title="Marcar todas como lidas"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Marcar todas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-xs text-slate-500 text-center py-6">Carregando...</p>
          ) : items.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Nenhuma observação nova.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((o) => {
                const isReq = o.type === "service_request";
                const Icon = isReq ? Wrench : MessageSquare;
                return (
                <li
                  key={o.id}
                  onClick={() => goTo(o)}
                  data-testid={`notif-item-${o.id}`}
                  className="group relative cursor-pointer px-3 py-2.5 hover:bg-slate-50 transition-colors flex gap-2"
                >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isReq ? "bg-amber-100" : "bg-violet-100"
                    }`}>
                      <Icon className={`w-4 h-4 ${isReq ? "text-amber-600" : "text-[#7c3aed]"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-slate-900 truncate flex items-center gap-1.5">
                        {o.school_name || "Escola"}
                        <span className={`inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                          isReq ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-[#7c3aed]"
                        }`}>
                          {isReq ? "Solicitação" : "Observação"}
                        </span>
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {isReq
                          ? (o.title || "Novo pedido")
                          : `Orçamento Nº ${o.budget_number || "?"}`}
                      </p>
                      <p className="text-[12px] text-slate-700 line-clamp-2 mt-0.5">
                        {o.text}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(o.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => markOne(o, e)}
                      data-testid={`notif-mark-${o.id}`}
                      title="Marcar como lida"
                      className="shrink-0 self-start w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                </li>
              );})}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
